import PaletteIcon from '@mui/icons-material/Palette';
import {
  Grid,
  Box,
  Typography,
  useMediaQuery,
  Button,
  Paper,
  Modal,
  Select,
  MenuItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useState, useEffect } from 'react';

import MetricsPanel from '@/components/dashboard/MetricsPanel';
import RecentActivities from '@/components/activities/RecentActivities';
import AIAssistant from '@/components/ai-assistant/AIAssistant';
import EmailSection from '@/components/email/EmailSection';
import { SunrisePanel } from '@/components/SunrisePanel';
import TasksWidget from '@/components/tasks/TasksWidget';

// Define theme colors
const burgundyTheme = {
  primary: '#9A2F35', // Burgundy/maroon red
  secondary: '#FFD4D4', // Light pink
  panelGradient: 'linear-gradient(135deg, #9A2F35 0%, #FFD4D4 100%)',
  panelText: '#FFFFFF', // White text
};

const navyTheme = {
  primary: '#1A365D', // Navy blue
  secondary: '#D4E5FF', // Light blue
  panelGradient: 'linear-gradient(135deg, #1A365D 0%, #D4E5FF 100%)',
  panelText: '#FFFFFF', // White text
};

// Color palette (24+ professional colors)
const colorPalette = [
  '#9A2F35',
  '#FFD4D4',
  '#1A365D',
  '#D4E5FF',
  '#2E3A59',
  '#F5F5F5',
  '#F59E0B',
  '#111827',
  '#64748B',
  '#E5E7EB',
  '#F3F4F6',
  '#E0E7FF',
  '#C7D2FE',
  '#F87171',
  '#FBBF24',
  '#34D399',
  '#60A5FA',
  '#6366F1',
  '#A78BFA',
  '#F472B6',
  '#F9A8D4',
  '#FECACA',
  '#FDE68A',
  '#D1FAE5',
  '#A7F3D0',
  '#6EE7B7',
  '#FDE68A',
  '#FCA5A5',
  '#FECACA',
  '#F3F4F6',
  '#E5E7EB',
  '#D1D5DB',
];

// Font list (professional, legal-friendly)
const fontList = [
  'Roboto Display',
  'Roboto',
  'Arial',
  'Georgia',
  'Times New Roman',
  'Lato',
  'Merriweather',
  'Montserrat',
  'Open Sans',
  'PT Serif',
  'Source Sans Pro',
  'Nunito',
  'Inter',
  'IBM Plex Sans',
  'Poppins',
  'Work Sans',
  'Quicksand',
  'DM Sans',
  'Noto Sans',
  'Ubuntu',
  'Cabin',
  'Fira Sans',
  'Heebo',
  'Assistant',
  'Mulish',
];

const fontSizeList = ['14px', '16px', '18px', '20px', '22px'];

const DashboardPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const userName = 'John'; // This should come from your auth context

  // Add state for the current theme
  const [currentTheme, setCurrentTheme] = useState(burgundyTheme);
  const [modalOpen, setModalOpen] = useState(false);
  const [mainBg, setMainBg] = useState(
    () => localStorage.getItem('mainBg') || burgundyTheme.primary
  );
  const [panelBg, setPanelBg] = useState(
    () => localStorage.getItem('panelBg') || burgundyTheme.secondary
  );
  const [font, setFont] = useState(() => localStorage.getItem('font') || 'Roboto Display');
  const [fontSize, setFontSize] = useState(() => localStorage.getItem('fontSize') || '16px');
  const [favoriteTheme, setFavoriteTheme] = useState(() => {
    const fav = localStorage.getItem('favoriteTheme');
    return fav ? JSON.parse(fav) : null;
  });

  // Toggle between themes
  const toggleTheme = () => {
    console.log('Theme toggle clicked');
    const newTheme = currentTheme === burgundyTheme ? navyTheme : burgundyTheme;
    console.log('Switching to theme:', newTheme);
    setCurrentTheme(newTheme);
  };

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  // On mount, apply persisted theme/font
  useEffect(() => {
    const persistedMainBg = localStorage.getItem('mainBg');
    const persistedPanelBg = localStorage.getItem('panelBg');
    const persistedFont = localStorage.getItem('font');
    const persistedFontSize = localStorage.getItem('fontSize');
    if (persistedMainBg) {
      document.documentElement.style.setProperty('--background-light', persistedMainBg);
      document.documentElement.style.setProperty('--topbar-bg', persistedMainBg);
    }
    if (persistedFont) {
      document.body.style.fontFamily = persistedFont;
    }
    if (persistedFontSize) {
      document.body.style.fontSize = persistedFontSize;
    }
    if (persistedMainBg || persistedPanelBg) {
      setCurrentTheme(theme => ({
        ...theme,
        primary: persistedMainBg || theme.primary,
        secondary: persistedPanelBg || theme.secondary,
        panelGradient: `linear-gradient(135deg, ${persistedMainBg || theme.primary} 0%, ${persistedPanelBg || theme.secondary} 100%)`,
        panelText: '#FFFFFF',
      }));
    }
  }, []);

  const handleApply = () => {
    localStorage.setItem('mainBg', mainBg);
    localStorage.setItem('panelBg', panelBg);
    localStorage.setItem('font', font);
    localStorage.setItem('fontSize', fontSize);
    setCurrentTheme({
      ...currentTheme,
      primary: mainBg,
      secondary: panelBg,
      panelGradient: `linear-gradient(135deg, ${mainBg} 0%, ${panelBg} 100%)`,
      panelText: '#FFFFFF',
    });
    document.documentElement.style.setProperty('--background-light', mainBg);
    document.documentElement.style.setProperty('--topbar-bg', mainBg);
    document.body.style.fontFamily = font;
    document.body.style.fontSize = fontSize;
    closeModal();
  };

  const handleReset = () => {
    setMainBg(burgundyTheme.primary);
    setPanelBg(burgundyTheme.secondary);
    setFont('Roboto Display');
    setFontSize('16px');
  };

  const handleSaveFavorite = () => {
    const fav = { mainBg, panelBg, font, fontSize };
    localStorage.setItem('favoriteTheme', JSON.stringify(fav));
    setFavoriteTheme(fav);
  };

  const handleLoadFavorite = () => {
    if (favoriteTheme) {
      setMainBg(favoriteTheme.mainBg);
      setPanelBg(favoriteTheme.panelBg);
      setFont(favoriteTheme.font);
      setFontSize(favoriteTheme.fontSize);
    }
  };

  return (
    <Box
      className="dashboard-container"
      sx={{ p: 3, fontFamily: `'${font}', Roboto, Arial, sans-serif` }}
    >
      {/* Theme Toggle Button */}
      <Button
        variant="contained"
        onClick={openModal}
        startIcon={<PaletteIcon />}
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 9999,
          borderRadius: 28,
          padding: '10px 20px',
          backgroundColor: currentTheme.primary,
          color: '#FFFFFF',
          boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
          '&:hover': {
            backgroundColor: currentTheme.secondary,
            color: currentTheme.primary,
          },
        }}
      >
        Customize Theme
      </Button>

      <Modal open={modalOpen} onClose={closeModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 540,
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: 24,
            p: 4,
            outline: 'none',
          }}
        >
          <Typography variant="h6" gutterBottom>
            Customize Theme
          </Typography>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            Main Background
          </Typography>
          <Grid container spacing={1} sx={{ mb: 2 }}>
            {colorPalette.map(color => (
              <Grid item xs={2} key={color}>
                <Box
                  onClick={() => setMainBg(color)}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: color,
                    border: mainBg === color ? '3px solid #333' : '2px solid #fff',
                    cursor: 'pointer',
                  }}
                />
              </Grid>
            ))}
          </Grid>
          <Typography variant="subtitle2">Panel Background</Typography>
          <Grid container spacing={1} sx={{ mb: 2 }}>
            {colorPalette.map(color => (
              <Grid item xs={2} key={color}>
                <Box
                  onClick={() => setPanelBg(color)}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: color,
                    border: panelBg === color ? '3px solid #333' : '2px solid #fff',
                    cursor: 'pointer',
                  }}
                />
              </Grid>
            ))}
          </Grid>
          <Typography variant="subtitle2">Font</Typography>
          <Select value={font} onChange={e => setFont(e.target.value)} fullWidth sx={{ mb: 2 }}>
            {fontList.map(f => (
              <MenuItem key={f} value={f} style={{ fontFamily: f }}>
                {f}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="subtitle2">Font Size</Typography>
          <Select
            value={fontSize}
            onChange={e => setFontSize(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          >
            {fontSizeList.map(size => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </Select>
          {/* Enhanced Preview */}
          <Box
            sx={{
              border: '1px solid #eee',
              borderRadius: 2,
              p: 2,
              mb: 2,
              fontFamily: `'${font}', Roboto, Arial, sans-serif`,
              fontSize,
            }}
          >
            {/* Mini Top Bar */}
            <Box
              sx={{
                height: 24,
                background: mainBg,
                borderRadius: 1,
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                px: 1,
              }}
            >
              <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700 }}>
                Top Bar
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', minHeight: 60 }}>
              {/* Mini Sidebar */}
              <Box
                sx={{
                  width: 48,
                  background: mainBg,
                  borderRadius: 1,
                  mr: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  py: 1,
                }}
              >
                <Box sx={{ width: 24, height: 24, bgcolor: '#fff', borderRadius: '50%', mb: 1 }} />
                <Box sx={{ width: 24, height: 24, bgcolor: '#fff', borderRadius: '50%', mb: 1 }} />
              </Box>
              {/* Mini Panel */}
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    background: `linear-gradient(135deg, ${mainBg} 0%, ${panelBg} 100%)`,
                    color: '#fff',
                    borderRadius: 2,
                    p: 1,
                    mb: 1,
                  }}
                >
                  Sunrise Panel
                </Box>
                <Box sx={{ background: panelBg, color: '#333', borderRadius: 1, p: 1 }}>
                  Panel Content
                </Box>
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1 }}>
            <Button onClick={handleReset}>Reset to Default</Button>
            <Button onClick={handleSaveFavorite}>Save as Favorite</Button>
            <Button onClick={handleLoadFavorite} disabled={!favoriteTheme}>
              Load Favorite
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={closeModal}>Cancel</Button>
            <Button variant="contained" onClick={handleApply}>
              Apply
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Welcome section with sunrise panel */}
      <Box className="welcome-section" sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Welcome back, {userName}
        </Typography>
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            width: '100%',
            '& .MuiPaper-root': {
              background: 'transparent',
              boxShadow: 'none',
              p: 0,
            },
          }}
        >
          <SunrisePanel title="Sunrise Report" subtitle="Daily activity" value="75%" />
        </Box>
      </Box>

      {/* Stats cards */}
      <Box className="stats-cards" sx={{ mb: 4 }}>
        <MetricsPanel />
      </Box>

      {/* Dashboard sections */}
      <Grid container spacing={3} sx={{ px: 0 }}>
        <Grid item xs={12} md={6}>
          <Box sx={{ height: isMobile ? 'auto' : '500px', minHeight: '400px' }}>
            <AIAssistant />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ height: isMobile ? 'auto' : '500px', minHeight: '400px' }}>
            <RecentActivities />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ height: isMobile ? 'auto' : '400px', minHeight: '350px' }}>
            <TasksWidget />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ height: isMobile ? 'auto' : '400px', minHeight: '350px' }}>
            <EmailSection />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage;
